import { AnalyticsDataFieldOptionsType, IInsuranceApp } from 'types/customTypes';
import { isEqual } from 'lodash';
import { isJsonString } from 'utils/isJsonString';
import { LoanApplicationEntities } from 'commonui';
import { fireApplicationInputChangeEvent } from './fireEventUtils';
import { IAnalyticsAppInputChange, IAnalyticsDataField } from './types';
import { AnalyticsEvent } from './index';

export interface IOnBlurArgs {
    isFeet?: boolean;
}
class AnalyticsInputChangeUtils {
    public runAnalytics = (
        fieldCode: string,
        formData: Record<string, string>,
        previousFormData: Record<string, string>,
        analyticsDataFieldMap: Map<string, IAnalyticsDataField>,
        insuranceApp: IInsuranceApp | undefined,
        initialLoad: boolean,
        isOnBlurChange: boolean,
        onBlurArgs?: IOnBlurArgs
    ): void => {
        const formFieldCode = this.getFormFieldCodeForInputChangeEvent(fieldCode, formData, analyticsDataFieldMap);
        const analyticsDataField = analyticsDataFieldMap.get(formFieldCode);
        if (analyticsDataField) {
            const isOnBlurOptionsType = this.isOnBlurOptionsType(analyticsDataField);
            if ((isOnBlurChange && !isOnBlurOptionsType) || (!isOnBlurChange && isOnBlurOptionsType)) {
                return;
            }
            const eventData = this.getAnalyticsInputChangeEventData(
                analyticsDataField,
                previousFormData[formFieldCode],
                formData[formFieldCode],
                initialLoad,
                onBlurArgs
            );
            if (eventData) {
                fireApplicationInputChangeEvent(
                    eventData.eventName,
                    eventData.fieldName,
                    eventData.fieldAction,
                    insuranceApp,
                    false,
                    eventData.allowDuplicateEvents
                );
            }
        }
    };

    public runInitialLoadAnalytics = (
        formData: Record<string, string>,
        previousFormData: Record<string, string>,
        analyticsDataFieldMap: Map<string, IAnalyticsDataField>,
        insuranceApp: IInsuranceApp | undefined
    ): void => {
        Object.entries(formData)
            .filter(([, value]) => value)
            .forEach((field) => {
                this.runAnalytics(
                    field[0],
                    formData,
                    previousFormData,
                    analyticsDataFieldMap,
                    insuranceApp,
                    true,
                    true
                );
            });
    };

    public getFormFieldCodeForInputChangeEvent = (
        fieldCode: string,
        formData: Record<string, string>,
        analyticsDataFieldMap: Map<string, IAnalyticsDataField>
    ): string => {
        let formFieldCode = fieldCode;
        const analyticsDataField = analyticsDataFieldMap.get(formFieldCode);
        analyticsDataFieldMap.forEach((val, key) => {
            const swapFieldCode = val?.googleAnalytics.swapField?.fieldCode;
            const swapFieldValue = val?.googleAnalytics.swapField?.value;
            const swapDisqualifier = analyticsDataField?.googleAnalytics.swapField?.swapDisqualifierValue;
            const shouldSwap = swapDisqualifier && swapDisqualifier === formData[formFieldCode] && formData[key];
            if (swapFieldCode === formFieldCode && swapFieldValue === formData[formFieldCode]) {
                if (!swapDisqualifier || shouldSwap) {
                    // swap fieldCodes in order to handle MODIFY_VALUE rules which update
                    // multiple fields at a time (lastUpdatedField may not match actual
                    // fieldCode user changed)
                    formFieldCode = key;
                }
            }
        });

        return formFieldCode;
    };

    public getApplicationInputChangeDataLayerData = (
        eventName: AnalyticsEvent | string,
        detail: IAnalyticsAppInputChange
    ): Record<string, string> => {
        return {
            event: eventName,
            stepName: detail.segmentTitle.replace('&', 'and'), // sending symbols in the data  might mess up things in GTM when using Regex comparisions
            channel: detail.channel,
            productCode: detail.productCode,
            premiumAmount: detail.premiumAmount,
            coverageAmount: detail.coverageAmount,
            fieldName: detail.fieldName,
            fieldAction: detail.fieldAction,
            ...(detail.riderProductCode && { 'rider.productCode': detail.riderProductCode }),
            ...(detail.riderCoverageAmount && { 'rider.coverageAmount': detail.riderCoverageAmount }),
            ...(detail.riderPremiumAmount && { 'rider.premiumAmount': detail.riderPremiumAmount })
        };
    };

    public getAnalyticsInputChangeEventData = (
        analyticsDataField: IAnalyticsDataField,
        currentValue: string,
        newValue: string,
        initialLoad: boolean,
        onBlurArgs?: IOnBlurArgs
    ):
        | { eventName: AnalyticsEvent; fieldName: string; fieldAction: string; allowDuplicateEvents?: boolean }
        | undefined => {
        const eventName = analyticsDataField.googleAnalytics.eventName as AnalyticsEvent;
        const {
            fieldName,
            fieldAction,
            allowDuplicateEvents = true
        } = this.getFieldParameters(analyticsDataField, currentValue, newValue, initialLoad, onBlurArgs);
        if (eventName && fieldName && fieldAction) {
            return { eventName, fieldName, fieldAction, allowDuplicateEvents };
        }

        return undefined;
    };

    public eventIsDuplicate = (evtName: AnalyticsEvent, detail: IAnalyticsAppInputChange): boolean => {
        if (Array.isArray(window.dataLayer)) {
            const currentEvent = this.getApplicationInputChangeDataLayerData(evtName, detail);
            const lastEvent = window.dataLayer[window.dataLayer.length - 1];
            if (lastEvent) {
                const eventCopy = { ...lastEvent };
                delete eventCopy['gtm.uniqueEventId'];
                if (isEqual(eventCopy, currentEvent)) {
                    return true;
                }
            }
        }

        return false;
    };

    public shouldTrackInputChange = (
        analyticsDataField: IAnalyticsDataField,
        args?: LoanApplicationEntities.IDataFieldChangeArgs
    ): boolean => {
        if (analyticsDataField.googleAnalytics.optionsType === AnalyticsDataFieldOptionsType.INPUT_STATUS) {
            return false;
        }
        if (analyticsDataField.fieldCode.toLowerCase().startsWith('group')) {
            return false;
        }

        // If value rule is sent, this implies the change was made indirectly, not directly changed by user
        return !args?.valueRule;
    };

    public shouldUpdateAnalyticsDataOnChange = (analyticsDataField: IAnalyticsDataField): boolean => {
        switch (analyticsDataField?.fieldType) {
            case 'multi-select-group':
            case 'nested-multi-select':
                /**
                 * some inputs, like checkboxes, should be updating the form data on change,
                 * since the changed value of the checkbox should be added to previous form data for comparison.
                 */
                return true;
            default:
                return false;
        }
    };

    private isOnBlurOptionsType = (analyticsDataField: IAnalyticsDataField): boolean => {
        return analyticsDataField.googleAnalytics.optionsType === AnalyticsDataFieldOptionsType.INPUT_STATUS;
    };

    private getFieldParameters = (
        analyticsDataField: IAnalyticsDataField,
        currentValue: string,
        newValue: string,
        initialLoad: boolean,
        onBlurArgs?: IOnBlurArgs
    ): {
        fieldName: string;
        fieldAction: string;
        allowDuplicateEvents?: boolean;
    } => {
        switch (analyticsDataField.googleAnalytics.optionsType) {
            case AnalyticsDataFieldOptionsType.CONTAINS:
                return this.containsOptionsType(analyticsDataField, currentValue, newValue, initialLoad);
            case AnalyticsDataFieldOptionsType.INPUT_STATUS:
                return this.useInputStatusOptionsType(
                    analyticsDataField,
                    currentValue,
                    newValue,
                    initialLoad,
                    onBlurArgs
                );
            case AnalyticsDataFieldOptionsType.LABEL:
                return this.labelOptionsType(analyticsDataField, newValue, initialLoad);
            default:
                return this.defaultOptionsType(analyticsDataField, newValue, initialLoad);
        }
    };

    private labelOptionsType = (
        analyticsDataField: IAnalyticsDataField,
        newValue: string,
        initialLoad: boolean
    ): { fieldName: string; fieldAction: string } => {
        const { fieldName } = analyticsDataField.googleAnalytics;
        let fieldAction = '';
        if (analyticsDataField.options?.length) {
            for (let i = 0; i < analyticsDataField.options.length; i++) {
                const option = analyticsDataField.options[i];
                if (newValue === option.value) {
                    fieldAction = option.label || '';
                    break;
                }
            }
        }
        if (initialLoad) {
            fieldAction = '';
        }

        return { fieldName, fieldAction };
    };

    private useInputStatusOptionsType = (
        analyticsDataField: IAnalyticsDataField,
        currentValue: string,
        newValue: string,
        initialLoad: boolean,
        onBlurArgs?: IOnBlurArgs
    ): { fieldName: string; fieldAction: string; allowDuplicateEvents: boolean } => {
        let { fieldName } = analyticsDataField.googleAnalytics;
        let previousValue = currentValue;
        let nextValue = newValue;
        let fieldAction = '';
        let allowDuplicateEvents = true;
        if (fieldName === 'Height') {
            if (onBlurArgs) {
                if (onBlurArgs.isFeet) {
                    fieldName = 'HeightFeet';
                    previousValue = isJsonString(previousValue) ? JSON.parse(previousValue).feet : '';
                    nextValue = isJsonString(nextValue) ? JSON.parse(nextValue).feet : '';
                } else {
                    fieldName = 'HeightInches';
                    previousValue = isJsonString(previousValue) ? JSON.parse(previousValue).inches : '';
                    nextValue = isJsonString(nextValue) ? JSON.parse(nextValue).inches : '';
                }
            }
        }
        if (initialLoad) {
            fieldAction = 'Prefilled';
        } else if (!!previousValue && previousValue !== nextValue) {
            fieldAction = 'Updated';
            allowDuplicateEvents = false;
        } else if (!!previousValue && previousValue === nextValue) {
            fieldAction = '';
        } else if (!previousValue && nextValue) {
            fieldAction = 'Completed';
        } else if (!nextValue) {
            fieldAction = 'Skipped';
        }

        return { fieldName, fieldAction, allowDuplicateEvents };
    };

    private containsOptionsType = (
        analyticsDataField: IAnalyticsDataField,
        currentValue: string,
        newValue: string,
        initialLoad: boolean
    ): { fieldName: string; fieldAction: string } => {
        let modifiedFieldName = analyticsDataField.googleAnalytics.fieldName;
        let fieldAction = '';
        for (let i = 0; i < analyticsDataField.googleAnalytics.options.length; i++) {
            const option = analyticsDataField.googleAnalytics.options[i];
            const currentValueIncludesOption = currentValue.includes(option.value);
            const newValueIncludesOption = newValue.includes(option.value);
            if (currentValueIncludesOption && !newValueIncludesOption && option.actionNegate) {
                if (option.fieldName) {
                    modifiedFieldName = option.fieldName;
                }
                fieldAction = option.actionNegate;
            }
            if (!currentValueIncludesOption && newValueIncludesOption) {
                if (option.fieldName) {
                    modifiedFieldName = option.fieldName;
                }
                fieldAction = option.action;
            }
        }
        if (initialLoad) {
            fieldAction = '';
        }

        return { fieldName: modifiedFieldName, fieldAction };
    };

    private defaultOptionsType = (
        analyticsDataField: IAnalyticsDataField,
        newValue: string,
        initialLoad: boolean
    ): { fieldName: string; fieldAction: string } => {
        const { fieldName } = analyticsDataField.googleAnalytics;
        let fieldAction = '';
        for (let i = 0; i < analyticsDataField.googleAnalytics.options.length; i++) {
            const option = analyticsDataField.googleAnalytics.options[i];
            if (newValue === option.value) {
                fieldAction = option.action;
                break; // once we find a match we can break the loop
            }
        }
        if (initialLoad) {
            fieldAction = '';
        }

        return { fieldName, fieldAction };
    };
}

export const analyticsInputChangeUtils = new AnalyticsInputChangeUtils();
